import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ZohoCustomerSearch — debounced picker for Zoho Books customer contacts.
 *
 * Extracted (Jul 2026) for the new Settings → Branch Mapping → Zoho Customer
 * tab. Deliberately NOT back-ported to the 4 pre-existing inline call sites
 * (DeliveryChallan / IBT / Dispatch) — those keep their own inline versions
 * to avoid churn in working code.
 *
 * Props:
 *   - value: { customer_id, customer_name } | null — current selection.
 *   - onChange: (next | null) → void
 *   - placeholder, testIdPrefix, disabled
 *
 * Behaviour:
 *   - When a selection exists, renders a pill with the customer name + a
 *     small (X) to clear. Typing is disabled while a selection is set.
 *   - When empty, renders a debounced search input. Results drop into a
 *     max-h list; click to select.
 */
const ZohoCustomerSearch = ({
  value,
  onChange,
  placeholder = "Search Zoho customer…",
  testIdPrefix = "zoho-customer-search",
  disabled = false,
}) => {
  const { token } = useAuthStore();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounced Zoho search
  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 1) {
      // Feb 2026 — was silent `< 2` gate; consumers now render the
      // visible hint below (see JSX). No behaviour change for < 1 char.
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/zoho/customers/search?q=${encodeURIComponent(q)}&limit=15`,
          { headers },
        );
        if (!cancelled) setResults(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, headers]);

  if (value && value.customer_id) {
    return (
      <div
        className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded px-3 py-2"
        data-testid={`${testIdPrefix}-selected`}
      >
        <div className="text-sm">
          <div className="font-medium">{value.customer_name || "(unnamed)"}</div>
          <div className="text-[11px] text-zinc-500 font-mono">{value.customer_id}</div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange(null)}
          data-testid={`${testIdPrefix}-clear`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
        <Input
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          className="pl-8"
          data-testid={`${testIdPrefix}-input`}
        />
        {searching && (
          <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 text-zinc-400 animate-spin" />
        )}
      </div>
      {results.length > 0 && (
        <div className="border rounded max-h-56 overflow-y-auto bg-white shadow-sm">
          {results.map((c) => (
            <button
              key={c.contact_id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm border-b last:border-b-0"
              onClick={() => {
                onChange({
                  customer_id: c.contact_id,
                  customer_name: c.contact_name,
                });
                setQuery("");
                setResults([]);
              }}
              data-testid={`${testIdPrefix}-option-${c.contact_id}`}
            >
              <div className="font-medium">{c.contact_name}</div>
              {c.company_name && c.company_name !== c.contact_name && (
                <div className="text-[11px] text-zinc-500">{c.company_name}</div>
              )}
            </button>
          ))}
        </div>
      )}
      {query.trim().length === 0 && !searching && (
        <div
          className="text-[11px] text-zinc-500 italic px-1"
          data-testid={`${testIdPrefix}-hint`}
        >
          Type to search Zoho customers…
        </div>
      )}
      {query.trim().length >= 1 && !searching && results.length === 0 && (
        <div
          className="text-[11px] text-zinc-500 px-1"
          data-testid={`${testIdPrefix}-no-results`}
        >
          No matches — try a shorter or different search term.
        </div>
      )}
    </div>
  );
};

export default ZohoCustomerSearch;
